import * as Sentry from '@sentry/react';
import { Scope } from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { isError, omit } from 'lodash';
import { LDClient, LDFlags, LD_KEY } from '~/providers';
import { ParsedQueryParams } from '~/providers/query-params';
import { SelfAttestationData } from '~/screens/vnm-terms';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { updateUser } from '~/services/update-user-meta-data';
import { BillingDataSource, EnrollmentDataSource } from '~/typings/context';
import {
  IS_PRODUCTION,
  IS_STAGING,
  IS_TEST,
  SENTRY_DSN,
  shouldUpdateMetaData,
} from '~/utils/constants';
import { logging } from './for-warbler';
import { store } from './store';
import { removeSensitiveData } from './xstate';
/* eslint-disable no-console */

/*
  the "identify" function sets up tracking identities for segment, launch darkly, and sentry

  we have different pieces of information at different stages of the funnel
  - user first enters the funnel with no utility id
  - user selects a utility or enters the funnel with a utility id
  - user creates an account or signs back in

  "identify" should be called whenever any of those three buckets changes

  docs:
    https://segment.com/docs/connections/spec/identify/
    https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
    https://docs.launchdarkly.com/sdk/features/identify#javascript
    https://docs.launchdarkly.com/home/users/anonymous-users
*/

interface IdentifyUserParams {
  id?: number;
  email?: string;
  utilityId?: number;
  promoCode?: string;
  funnelTrack?: string;
  state?: string;
}

type LmiStatus = 'isLmi' | 'maybeLmi' | 'notLmi';

interface CommonPayload {
  email: string;
  event_message: string;
  first_name: string;
  inviteCode?: string;
  last_name: string;
  phone?: string | null | undefined;
  promo_code?: string;
  state?: string;
  userIp: string;
}

const identifyWithActiveUser = async (user: IdentifyUserParams, funnel: string, segment: any) => {
  const nonLmi = getContext()?.queryParams?.unparsedQueryParams?.nonLmi;
  const { id, email, utilityId, promoCode, funnelTrack, state } = user;
  const idString = `${user.id}`;
  const custom: any = {
    funnel,
    utilityId,
    funnelTrack,
    state: state?.toLowerCase(),
    ...(promoCode ? { promoCode } : {}),
  };
  if (nonLmi !== undefined && typeof nonLmi === 'boolean') custom.nonLmi = nonLmi;
  segment?.identify(id, { email, utilityId, promoCode, funnelTrack });
  Sentry.configureScope(scope => scope.setUser({ id: idString, email }));
  const ldFlagSet = await LDClient.identify({
    key: idString,
    email,
    custom,
  });
  updateLanguage();
  return ldFlagSet;
};

const identifyWithSelectedUtility = async (
  user: Omit<IdentifyUserParams, 'id' | 'email'>,
  funnel: string,
  segment: any,
) => {
  const { utilityId, promoCode, funnelTrack } = user;
  const custom: any = {
    funnel,
    utilityId,
    funnelTrack,
    ...(promoCode ? { promoCode } : {}),
  };
  segment?.identify({ utilityId, promoCode, funnelTrack });
  const ldFlagSet = await LDClient.identify({ key: LD_KEY, custom });
  updateLanguage();
  return ldFlagSet;
};

const identifyWithPromoCode = async (
  user: Omit<IdentifyUserParams, 'id' | 'email' | 'utilityId'>,
  funnel: string,
  segment: any,
) => {
  const { promoCode, funnelTrack } = user;
  const custom: any = {
    funnel,
    funnelTrack,
    promoCode,
  };
  segment?.identify({ promoCode, funnelTrack });
  const ldFlagSet = await LDClient.identify({ key: LD_KEY, custom });
  updateLanguage();
  return ldFlagSet;
};

const identifyWithSelectedFunnel = async (funnel: string, segment: any) => {
  const nonLmi = getContext()?.queryParams?.unparsedQueryParams?.nonLmi;
  const custom: any = { funnel };
  segment?.identify({});
  if (nonLmi !== undefined && typeof nonLmi === 'boolean') custom.nonLmi = nonLmi;
  const ldFlagSet = await LDClient.identify({ key: LD_KEY, custom });
  toggleLocalize();
  return ldFlagSet;
};

export const identify = (user: IdentifyUserParams) => {
  if (IS_TEST) return;
  const funnel = store.get('ACTIVE_FUNNEL_ID')!;
  const segment = window.analytics;
  try {
    if ('id' in user && user.id) {
      return identifyWithActiveUser(user, funnel, segment);
    } else if ('utilityId' in user && user.utilityId) {
      return identifyWithSelectedUtility(user, funnel, segment);
    } else if ('promoCode' in user && user.promoCode) {
      return identifyWithPromoCode(user, funnel, segment);
    } else {
      return identifyWithSelectedFunnel(funnel, segment);
    }
  } catch (e) {
    trackError(new Error(`Error calling identify: ${isError(e) ? e.message : e}`));
    return Promise.reject(e);
  }
};

export const updateTrackingIdentity = (
  packet?: FetchFunnelPacketResponse,
  utilityId?: number,
  promoCode?: string,
  funnelTrack?: string,
) => {
  // Define the object with optional email and id properties
  const identityObject: {
    email?: string;
    id?: number;
    utilityId: any;
    promoCode: any;
    funnelTrack: any;
  } = {
    utilityId: packet?.utility.id ?? utilityId,
    promoCode: packet?.user?.promoCode ?? promoCode,
    funnelTrack: packet?.utility.funnelTrack ?? funnelTrack,
    id: packet?.user.id,
    email: packet?.user.email,
  };

  return identify(identityObject);
};

export const initializeTracking = () => {
  if (IS_PRODUCTION || IS_STAGING)
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: process.env.ENVIRONMENT,
      integrations: [new BrowserTracing()],
      tracesSampler: samplingContext => {
        if (IS_PRODUCTION && samplingContext.transactionContext.op === 'pageload') {
          return 0.5;
        } else {
          return 0;
        }
      },
      /**
       * This error is blowing up our Sentry error budget
       * REF: https://arcadia-power-i2.sentry.io/issues/4723424449/events/?cursor=0%3A100%3A0&environment=production&referrer=alerts-related-issues-issue-stream
       * We have reported to Sentry here: https://github.com/getsentry/sentry-javascript/issues/9884 and looks like this is coming from our codebase.
       * So far we have not been able to reproduce this issue locally and this is not impacting our users.
       */
      ignoreErrors: ["Can't find variable: ttkv", 'ttkv is not defined'],
    });
};

// errors that we'd like to group together in Sentry
const fingerprints = ['already exists in our records'];
const setSentryFingerprint = (scope: Sentry.Scope, error?: Error) => {
  const errorMessage = error?.message;
  if (!errorMessage) return;

  const fingerprint = fingerprints.find(f => errorMessage.includes(f));
  scope.setFingerprint([fingerprint || errorMessage]);
};

const getContext = () => {
  const id = store.get('ACTIVE_FUNNEL_ID');
  const activeFunnelRoute = store.get('ACTIVE_FUNNEL_ROUTE');
  const funnel = store.getFunnel(id!);
  const context = funnel?.context || {};
  return removeSensitiveData({
    ...context,
    activeFunnelRoute,
    funnelId: id,
    funnelStep: funnel?.value,
    // TODO: would be nice to have microStep
  });
};

export const trackError = (
  error: Error,
  {
    extras = {},
    tags = {},
  }: {
    extras?: Parameters<Scope['setExtras']>[0];
    tags?: Parameters<Scope['setTags']>[0];
  } = {},
) => {
  if (IS_TEST) return;
  return Sentry.withScope(scope => {
    scope.setTags(tags);
    scope.setExtras({ ...extras, ...getContext() });
    setSentryFingerprint(scope, error);
    Sentry.captureException(error);
  });
};

export const trackPageView = (properties?: Record<string, any>) => {
  if (IS_TEST) return;
  try {
    window.analytics?.page(undefined, properties);
  } catch (e) {
    console.error(e);
  }
};

const toggleLocalize = () => {
  const flagOn = LDClient.variation(LDFlags.showLocalizeWidget);
  if (flagOn) window.Localize?.showWidget();
  else window.Localize?.hideWidget();
};

export const updateLanguage = () => {
  const spanishLanuage = LDClient.variation(LDFlags.showSpanishLanguage);
  if (spanishLanuage) {
    window.Localize?.setLanguage('es');
  }
  toggleLocalize();
};

export type SegmentEvent =
  | {
      type: 'track_queryParams_decoding_error';
      encodedNumber: string;
      queryParam: string;
      email: string;
    }
  | {
      type: 'track_ld_fetch_feature_flags_error';
      funnelTrack?: string;
      utilityId?: number;
      errorClass: string;
      errorMessage: string;
    }
  | {
      type: 'track_query_params';
      funnelPacket: FetchFunnelPacketResponse;
      userIp: string;
      utilityId?: number;
      queryParams: ParsedQueryParams;
    }
  | {
      type: 'track_micro_learning_screen';
      screen?: string;
      utilityId?: number;
      zipCode?: string;
    }
  | {
      type: 'track_next_button_pressed';
      screen?: string;
      utilityId?: number;
      zipCode?: string;
    }
  | {
      type: 'track_skip_button_pressed';
      screen?: string;
      utilityId?: number;
      zipCode?: string;
    }
  | {
      type: 'track_done_button_pressed';
      screen?: string;
      utilityId?: number;
      zipCode?: string;
    }
  | {
      type: 'utility_step_started';
      utilityId?: number;
      zipCode?: string;
    }
  | {
      type: 'utility_step_completed';
      utilityId: number;
      zipCode: string;
      funnelTrack?: string;
    }
  | {
      type: 'user_step_started';
      utilityId: number;
      funnelTrack?: string;
    }
  | {
      type: 'user_step_completed';
      userIp: string;
      utilityName: string;
      zipCode: string;
      state: string;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'enrollment_step_started';
      enrollmentDataSourceOptions: EnrollmentDataSource[];
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'enrollment_step_completed';
      enrollmentDataSourceSelection: EnrollmentDataSource;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'utility_connect_result';
      utilityCredentialId: number;
      utilityConnectStatus: string;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'alternate_enrollment_step_started';
      enrollmentStep: EnrollmentDataSource;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'billing_step_started';
      billingExperienceOptions: BillingDataSource[];
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'billing_step_completed';
      billingExperienceSelection: BillingDataSource;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'payment_method_created';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'calendly_step_started';
      funnelPacket: FetchFunnelPacketResponse;
      userIp: string;
      promo_code?: string;
    }
  | {
      type: 'calendly_step_completed';
      funnelPacket: FetchFunnelPacketResponse;
      userIp: string;
      promo_code?: string;
    }
  | {
      type: 'acuity_step_started';
      funnelPacket: FetchFunnelPacketResponse;
      userIp: string;
      promo_code?: string;
    }
  | {
      type: 'acuity_step_completed';
      funnelPacket: FetchFunnelPacketResponse;
      userIp: string;
      promo_code?: string;
    }
  | {
      type: 'legal_step_started';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'legal_step_completed';
      contractIds: number[];
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'review_step_started';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'lmi_review_step_started';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'track_billing_decider_step_started';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'track_no_billing_step_started';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'review_step_completed';
      contractIds: number[];
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'lmi_review_step_completed';
      contractIds: number[];
      selfAttestationData: SelfAttestationData | null;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'lmi_attestation_submitted';
      selfAttestationData: SelfAttestationData | null;
      documentTypes: string[];
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'lmi_review_step_skipped';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'upload_lmi_proof_docs_step_started';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'upload_lmi_proof_docs_step_completed';
      funnelPacket: FetchFunnelPacketResponse;
      documentTypes: string[];
    }
  | {
      type: 'funnel_completed';
      track?: string;
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'resuming_funnel_started';
      accountId: number;
      planTemplateId: number;
      userId: number;
      requirements?: string;
    }
  | {
      type: 'resume_signup_email_sent';
      userId: number;
      step: string;
    }
  | {
      type: 'resume_signup_email_with_account_number_sent';
      userId: number;
      step: string;
    }
  | {
      type: 'resume_signup_email_with_bill_upload_sent';
      userId: number;
      step: string;
    }
  | {
      type: 'utility_account_selection_step_started';
      userId: number;
    }
  | {
      type: 'utility_account_selection_chosen';
      selectedUtilityAccountId: number;
      userId: number;
    }
  | {
      type: 'utility_account_creation_step_started';
      userId: number;
    }
  | {
      type: 'utility_account_creation_step_completed';
      userId: number;
      utilityId: number;
      utilityAccountId: number;
    }
  | {
      type: 'coming_from_dashboard';
      reason: string;
      funnelPacket: FetchFunnelPacketResponse;
      funnelId: string;
      planTemplateId: number;
    }
  | {
      type: 'payment_hold_result';
      funnelPacket: FetchFunnelPacketResponse;
      message?: string;
      paymentMethodId: number;
      result: string;
    }
  | {
      type: 'track_sms_opt_in_preference';
      value: boolean;
      userId?: number;
    }
  | {
      type: 'track_oregon_lmi_link_clicked';
      funnelPacket: FetchFunnelPacketResponse;
      clicked: 'oregon_csp_website' | 'support_email';
    }
  | {
      type: 'track_docusign_status';
      funnelPacket: FetchFunnelPacketResponse;
      additional: CommonPayload & {
        docusignStatus: string;
      };
    }
  | {
      type: 'user_opted_yes_for_lmi';
      funnelPacket: FetchFunnelPacketResponse;
      additional: CommonPayload & {
        userLmiInput: LmiStatus;
      };
    }
  | {
      type: 'user_opted_no_for_lmi';
      funnelPacket: FetchFunnelPacketResponse;
      additional: CommonPayload & {
        userLmiInput: LmiStatus;
      };
    }
  | {
      type: 'cs_unavailable';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'user_opted_maybe_for_lmi';
      funnelPacket: FetchFunnelPacketResponse;
      additional: CommonPayload & {
        userLmiInput: LmiStatus;
      };
    }
  | {
      type: 'lmi_document_upload';
      funnelPacket: FetchFunnelPacketResponse;
      additional: {
        selected_function: string;
      };
    }
  | {
      type: 'lmi_document_skipped';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'utility_connect_timed_out';
      funnelPacket: FetchFunnelPacketResponse;
    }
  | {
      type: 'secondary_identifier_added';
      funnelPacket: FetchFunnelPacketResponse;
      additional?: {
        secondary_identifier_field: string;
        account_number?: string;
        secondary_account_identifier?: string;
      };
    }
  | {
      type: 'welcome_call_scheduled';
      funnelPacket: FetchFunnelPacketResponse;
      additional: {
        buisnessName: string;
        scheduled_time: string;
        scheduled_date: string;
        account_number: string | null;
      };
    }
  | {
      type: 'non_lmi_user';
      funnelPacket: FetchFunnelPacketResponse;
      additional: { account_number: string | null };
    }
  | {
      type: 'docusign_lmi_user_signed';
      funnelPacket: FetchFunnelPacketResponse;
      additional: {
        lmiStatus: string;
        account_number: string | null;
      };
    }
  | {
      type: 'lmi_proof_options_value';
      funnelPacket: FetchFunnelPacketResponse;
      lmiProofDocsState: Array<string>;
    }
  | {
      type: 'payment_method_skipped_for_test_user';
      funnelPacket: FetchFunnelPacketResponse;
      additional: {
        email: string;
      };
    };

export const sendToSegment = (event: SegmentEvent) => {
  if (IS_TEST) return;
  try {
    const funnelId = 'funnelId' in event ? event.funnelId : store.get('ACTIVE_FUNNEL_ID');
    const payload: Record<string, any> = {
      ...omit(event, ['type', 'funnelPacket']),
      funnelId,
      version: 'v2',
    };

    // default payload values for all events
    if ('funnelPacket' in event) {
      payload.state = event.funnelPacket.utility.state.abbreviation;
      payload.userId = event.funnelPacket.user.id;
      payload.utilityAccountId = event.funnelPacket.utilityAccount.id;
      payload.utilityId = event.funnelPacket.utility.id;
      payload.utilityName = event.funnelPacket.utility.displayName;
      payload.zipCode = event.funnelPacket.user.zipCode;
      payload.funnelTrack = event.funnelPacket.utility.funnelTrack;
    }

    if (!IS_PRODUCTION) {
      const message = ['%csegment', 'color: #9ac2b1; font-weight: bold;', event.type];
      logging.groupCollapsed(...message);
      logging.log('payload:', payload);
      logging.groupEnd(...message);
    }
    if (shouldUpdateMetaData(event.type)) {
      const updateData: {
        event: string;
        funnelCompletedTimeStamp?: string;
        timezone?: string;
      } = {
        event: event.type,
      };
      if (event.type === 'user_step_completed') {
        updateData.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      if (event.type === 'funnel_completed') {
        updateData.funnelCompletedTimeStamp = new Date().toISOString();
      }
      updateUser(payload.userId, updateData);
    }
    window.analytics?.track(event.type, payload);
    keepOldEventsAlive(event);
  } catch (e) {
    console.error(e);
  }
};

// keeps backwards compatibility with marketing triggers that rely on old event names and payloads
const keepOldEventsAlive = (event: SegmentEvent) => {
  const track = window.analytics?.track;
  if (!track) return;

  // state, utilityName, and zipCode are needed for the impact partnership
  // impact cares about accountCreated, utilityCredsInitialSubmitted, paymentMethodAdded, and funnelCompleted
  // funnelCompleted is already supported (fired before keepOldEventsAlive is called)
  const createPayload = (event: SegmentEvent) => {
    if ('funnelPacket' in event) {
      return {
        funnelId: store.get('ACTIVE_FUNNEL_ID'),
        state: event.funnelPacket.utility.state.abbreviation,
        userId: event.funnelPacket.user.id,
        utilityName: event.funnelPacket.utility.displayName,
        zipCode: event.funnelPacket.user.zipCode,
      };
    } else {
      return {};
    }
  };

  if (event.type === 'utility_step_completed') {
    track('utility_selected', createPayload(event));
  }
  if (event.type === 'user_step_completed') {
    track('account_created', createPayload(event));
  }
  if (event.type === 'utility_connect_result') {
    track('utility_creds_initial_submitted', createPayload(event));
  }
  if (event.type === 'utility_connect_result' && event.utilityConnectStatus === 'verified') {
    track('utility_creds_verified_resulted', createPayload(event));
  }
  if (event.type === 'payment_method_created') {
    track('payment_method_added', createPayload(event));
  }
};

// TODO: legacy - remove
export const trackEvent = (
  eventName: LegacyTrackingEvent,
  properties: Record<string, any> = {},
) => {
  if (IS_TEST) return;
  try {
    window.analytics?.track(eventName, { ...properties, ...getContext() });
  } catch (e) {
    console.error(e);
  }
};

export const fetchAndStoreUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    store.set('CLIENT_IP', data.ip);
    return data.ip;
  } catch (error) {
    // We don't want to return custom error messages here and it's fine
    // to send an empty string if we can't get the IP
    return '';
  }
};

export const getUserIpFromStore = () => {
  const ipAddr = store.get('CLIENT_IP');
  if (ipAddr) {
    return ipAddr;
  } else {
    return 'Unable to fetch Client IP';
  }
};

export const getUserIdFromStore = () => {
  const userIdFromStore = store.get('USER_ID');
  return userIdFromStore || null;
};

// TODO: LEGACY EVENTS - need to be deleted or transferred to SegmentEvent
export const TRACK = {
  emailAlreadyExistsResulted: 'email_already_exists_resulted',
  emailCategorizedAsInvalid: 'email_categorized_as_invalid',
  expired: 'expired_screen',
  ipaContractReturnedFromDocusign: 'ipa_contract_returned_from_docusign',
  learnMoreCsClicked: 'learn_more_cs_clicked',
  supportChatClicked: 'support_chat_clicked',
  supportLinkClicked: 'support_link_clicked',
  unexpected: 'unexpected_screen',
  utilityCredsDuplicateResulted: 'utility_creds_duplicate_resulted',
} as const;
export type LegacyTrackingEvent = (typeof TRACK)[keyof typeof TRACK];

export const allowConsolidatedUtilityAccount = promoCode => {
  if (!promoCode) {
    return false;
  }
  const promoCodeBlacklist = LDClient.variation(LDFlags.epBlacklistedAffiliatePromoCodes);
  return !promoCodeBlacklist.some(blacklistedCode => promoCode.includes(blacklistedCode));
};
